import type { MatterRead } from '@/matter/store/schemas'
import { MatterViewerRole } from '@/matter/store/schemas'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'

type MatterSubject = Pick<MatterRead, 'viewer_role'> | null | undefined
type MatterMembership = Pick<MatterRead, 'viewer_role' | 'accepted_at'> | null | undefined

/** Can edit the matter. */
export function canEditMatter(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can delete the matter. */
export function canDeleteMatter(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can edit the matter settings. */
export function canEditMatterSettings(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can add/remove members and change roles. */
export function canEditMatterMembers(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can view the members list. */
export function canViewMatterMembers(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner || matter?.viewer_role === MatterViewerRole.Collaborator
}

/** Can create a new AI Assistant chat. */
export function canCreateChat(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner || matter?.viewer_role === MatterViewerRole.Collaborator
}

/** Can view AI Assistant chat. */
export function canViewChats(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner || matter?.viewer_role === MatterViewerRole.Collaborator
}

/** Whether the viewer can access the matter's Medical Chronology. */
export function canViewMedChron(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true

  const viewerRole = matter?.viewer_role
  return (
    viewerRole === MatterViewerRole.Owner || (viewerRole === MatterViewerRole.Collaborator && kGetFeatureFlagStatus(kFeatureFlags.COLLABORATOR_MEDCHRON_ACCESS))
  )
}

/** Can view the matter Timeline. Owner-only until Timeline gets its own collaborator permission. */
export function canViewTimeline(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can view Medical Bills. Owner-only until Medical Bills gets its own collaborator permission. */
export function canViewMedicalBills(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can move matter files to a different matter. */
export function canMoveMatterFilesExternally(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Can move or assign an existing chat into the matter. */
export function canMoveChatToMatter(matter: MatterSubject): boolean {
  if (!kGetFeatureFlagStatus(kFeatureFlags.COLLABORATION)) return true
  return matter?.viewer_role === MatterViewerRole.Owner
}

/** Returns true if the user is an active member of this matter (owner or accepted collaborator). */
export function isActiveMatterMember(matter: MatterMembership): boolean {
  if (matter?.viewer_role === MatterViewerRole.Owner) return true
  return matter?.viewer_role === MatterViewerRole.Collaborator && matter.accepted_at !== null
}
